import { useState, useEffect, useCallback, useRef } from "react";
import { useLocalFilters } from "../../../../hooks/useLocalFilters";
import { useOcelMetadata } from "../../../../stores/OcelContext";

const EMPTY_TILE_FILTER = { enabled: false, mode: "include", matchType: "any", conditions: [] };

const STORAGE_KEY = (activeFile) => `absoluteDelayTimeline_v2_${activeFile || "default"}`;

function loadFromStorage(activeFile) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(activeFile));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveToStorage(activeFile, state) {
  try {
    localStorage.setItem(STORAGE_KEY(activeFile), JSON.stringify(state));
  } catch {
    // storage full — silently ignore
  }
}

export function useAbsoluteDelayTimeline(activeFile, initialSettings) {
  const {
    localFilters, updateLocalFilter, clearLocalFilters, hasLocalFilters, localFilterCount,
    api, currentFilters, startDraftMode, applyDraftFilters, cancelDraftFilters,
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);
  const metadata = useOcelMetadata(activeFile);

  const datasetEnd = metadata?.date_range?.end ? metadata.date_range.end.slice(0, 10) : null;
  const objectTypes = metadata?.object_types ? Object.keys(metadata.object_types) : [];

  // ── Settings state (persisted) ──────────────────────────────────────────────
  const [componentTitle, setComponentTitle] = useState("");
  const [objectType, setObjectType] = useState("");
  const [refDateType, setRefDateType] = useState("object_attribute");
  const [refDateValue, setRefDateValue] = useState("");
  const [refEventAttributeSource, setRefEventAttributeSource] = useState(null); // { activity, attribute } | null
  const [actualDateType, setActualDateType] = useState("object_attribute");
  const [actualDateValue, setActualDateValue] = useState([]);
  const [actualEventAttributeSources, setActualEventAttributeSources] = useState([]); // Array<{ activity, attribute }>
  const [tileEventAttributeFilter, setTileEventAttributeFilter] = useState(EMPTY_TILE_FILTER);

  // ── Display state (persisted, pure client-side transforms of the raw daily data) ──
  const [viewMode, setViewMode] = useState("granularity"); // "granularity" | "daterange"
  const [granularity, setGranularity] = useState("day");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [aggregation, setAggregation] = useState("average"); // min | median | average | max | sum
  const [includeMode, setIncludeMode] = useState("late_only"); // late_only | all
  const [rollingWindowDays, setRollingWindowDays] = useState(30); // only applied when aggregation !== "median"
  const [thresholdType, setThresholdType] = useState(null);
  const [thresholdTarget, setThresholdTarget] = useState(null);
  const [thresholdWarningPercentage, setThresholdWarningPercentage] = useState(null);
  const [thresholdLower, setThresholdLower] = useState(null);
  const [thresholdUpper, setThresholdUpper] = useState(null);

  // ── Data state ───────────────────────────────────────────────────────────────
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isFirstFilterRender = useRef(true);

  // ── Restore from localStorage on mount ──────────────────────────────────────
  useEffect(() => {
    const saved = loadFromStorage(activeFile);
    if (saved) {
      if (saved.componentTitle !== undefined) setComponentTitle(saved.componentTitle);
      if (saved.objectType !== undefined) setObjectType(saved.objectType);
      if (saved.refDateType !== undefined) setRefDateType(saved.refDateType);
      if (saved.refDateValue !== undefined) setRefDateValue(saved.refDateValue);
      if (saved.refEventAttributeSource !== undefined) setRefEventAttributeSource(saved.refEventAttributeSource);
      if (saved.actualDateType !== undefined) setActualDateType(saved.actualDateType);
      if (saved.actualDateValue !== undefined) setActualDateValue(saved.actualDateValue);
      if (saved.actualEventAttributeSources !== undefined) setActualEventAttributeSources(saved.actualEventAttributeSources);
      if (saved.tileEventAttributeFilter !== undefined) setTileEventAttributeFilter(saved.tileEventAttributeFilter);
      if (saved.viewMode !== undefined) setViewMode(saved.viewMode);
      if (saved.granularity !== undefined) setGranularity(saved.granularity);
      if (saved.dateRangeStart !== undefined) setDateRangeStart(saved.dateRangeStart);
      if (saved.dateRangeEnd !== undefined) setDateRangeEnd(saved.dateRangeEnd);
      if (saved.aggregation !== undefined) setAggregation(saved.aggregation);
      if (saved.includeMode !== undefined) setIncludeMode(saved.includeMode);
      if (saved.rollingWindowDays !== undefined) setRollingWindowDays(saved.rollingWindowDays);
      if (saved.thresholdType !== undefined) setThresholdType(saved.thresholdType);
      if (saved.thresholdTarget !== undefined) setThresholdTarget(saved.thresholdTarget);
      if (saved.thresholdWarningPercentage !== undefined) setThresholdWarningPercentage(saved.thresholdWarningPercentage);
      if (saved.thresholdLower !== undefined) setThresholdLower(saved.thresholdLower);
      if (saved.thresholdUpper !== undefined) setThresholdUpper(saved.thresholdUpper);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Persist to localStorage on every state change ───────────────────────────
  useEffect(() => {
    saveToStorage(activeFile, {
      componentTitle, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources,
      tileEventAttributeFilter, viewMode, granularity, dateRangeStart, dateRangeEnd,
      aggregation, includeMode, rollingWindowDays,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper,
    });
  }, [activeFile, componentTitle, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources,
      tileEventAttributeFilter, viewMode, granularity, dateRangeStart, dateRangeEnd,
      aggregation, includeMode, rollingWindowDays,
      thresholdType, thresholdTarget, thresholdWarningPercentage, thresholdLower, thresholdUpper]);

  // ── Configuration completeness ───────────────────────────────────────────────
  const refReady = refDateType === "event_attribute"
    ? !!(refEventAttributeSource?.activity && refEventAttributeSource?.attribute)
    : !!refDateValue;
  const actualReady = actualDateType === "event_attribute"
    ? actualEventAttributeSources.length > 0
    : actualDateValue.length > 0;
  const hasConfig = !!(objectType && refReady && actualReady);

  // ── Data fetching — only re-fetches on date-config / global-filter changes ───
  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change) must not overwrite a
  // newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!hasConfig) return;
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.post(
        "/kpi/absolute-delay-timeline",
        {
          object_type: objectType,
          ref_date_type: refDateType,
          ref_date_value: refDateType === "event_attribute" ? null : refDateValue,
          ref_event_attribute_source: refDateType === "event_attribute" ? refEventAttributeSource : undefined,
          actual_date_type: actualDateType,
          actual_date_value: actualDateType === "event_attribute" ? null : actualDateValue,
          actual_event_attribute_sources: actualDateType === "event_attribute" ? actualEventAttributeSources : undefined,
          kpi_title: componentTitle || "Absoluter Verzug über Zeit",
          tileEventAttributeFilter: tileEventAttributeFilter.enabled ? tileEventAttributeFilter : undefined,
          rolling_window_days: rollingWindowDays,
          ...(datasetEnd && { period_end: datasetEnd }),
        },
        {},
        "Absoluter Verzug über Zeit"
      );
      if (seq !== fetchSeqRef.current) return;
      setChartData(result);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      setError(err?.message || "Fehler beim Laden der Daten");
      setChartData(null);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, hasConfig, objectType, refDateType, refDateValue, refEventAttributeSource,
      actualDateType, actualDateValue, actualEventAttributeSources,
      tileEventAttributeFilter, componentTitle, datasetEnd, rollingWindowDays]);

  // Re-fetch when local-or-global filters change (skip mount)
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    if (hasConfig) fetchData();
  }, [currentFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // date-source settings
    componentTitle, setComponentTitle,
    objectType, setObjectType,
    refDateType, setRefDateType,
    refDateValue, setRefDateValue,
    refEventAttributeSource, setRefEventAttributeSource,
    actualDateType, setActualDateType,
    actualDateValue, setActualDateValue,
    actualEventAttributeSources, setActualEventAttributeSources,
    tileEventAttributeFilter, setTileEventAttributeFilter,
    objectTypes,
    metadata,
    // local filters (standard per-tile override — all-or-nothing vs. global, see useLocalFilters)
    localFilters, updateLocalFilter, clearLocalFilters, hasLocalFilters, localFilterCount,
    startDraftMode, applyDraftFilters, cancelDraftFilters,
    // display settings
    viewMode, setViewMode,
    granularity, setGranularity,
    dateRangeStart, setDateRangeStart,
    dateRangeEnd, setDateRangeEnd,
    aggregation, setAggregation,
    includeMode, setIncludeMode,
    rollingWindowDays, setRollingWindowDays,
    thresholdType, setThresholdType,
    thresholdTarget, setThresholdTarget,
    thresholdWarningPercentage, setThresholdWarningPercentage,
    thresholdLower, setThresholdLower,
    thresholdUpper, setThresholdUpper,
    // data
    chartData,
    loading,
    error,
    hasConfig,
    fetchData,
    datasetEnd,
  };
}
